import { resolveEssenceExchangeValue, resolveEssenceToTake } from './essence-exchange';
import type {
    CommissionGoal,
    CommissionGoalProgress,
    CommissionGoalRequirement,
} from './types/commission';
import type { EssencePool } from './types/essence';
import { getAllEssenceTypes } from './types/essence';

/**
 * Commission goals: one evaluator shared by the crucible host (live progress
 * and the heart lock) and the server (completion and hand-over at the end of
 * a run), so what the HUD shows and what pays out always agree.
 */

/** What a run knows that goals are judged against, for one character. */
export interface CommissionGoalFacts {
    /** Essence this character collected in the run. */
    readonly essence: Partial<EssencePool>;
    /** Essence the whole party collected in the run, for 'party'-scope goals. */
    readonly partyEssence: Partial<EssencePool>;
    readonly isHeartShattered: boolean;
}

/**
 * Each goal's state for one character, in the goals' order. A goal is locked
 * until its requirements are met, then complete once its own condition is.
 * A goal on a requirement cycle stays locked (validation rejects cycles).
 */
export function resolveCommissionGoals(
    goals: readonly CommissionGoal[],
    facts: CommissionGoalFacts,
): CommissionGoalProgress[] {
    const goalsById = new Map(goals.map((goal) => [goal.id, goal]));
    const evaluated = new Map<string, CommissionGoalProgress>();
    const evaluating = new Set<string>();

    const evaluate = (goal: CommissionGoal): CommissionGoalProgress => {
        const cached = evaluated.get(goal.id);
        if (cached) {
            return cached;
        }
        if (evaluating.has(goal.id)) {
            return { goalId: goal.id, status: 'locked' };
        }
        evaluating.add(goal.id);

        const isRequirementMet = (requirement: CommissionGoalRequirement): boolean => {
            const required = goalsById.get(requirement.goal);
            return required !== undefined && evaluate(required).status === 'complete';
        };
        const unlocked = (goal.requires ?? []).every(isRequirementMet);
        const measured = measureGoal(goal, facts);
        const progress: CommissionGoalProgress = {
            goalId: goal.id,
            status: !unlocked ? 'locked' : measured.isMet ? 'complete' : 'active',
            ...(measured.target !== undefined ? { current: measured.current, target: measured.target } : {}),
        };

        evaluating.delete(goal.id);
        evaluated.set(goal.id, progress);
        return progress;
    };

    return goals.map(evaluate);
}

/** Whether a goal's own condition holds, ignoring its requirements. */
function measureGoal(goal: CommissionGoal, facts: CommissionGoalFacts): { isMet: boolean; current?: number; target?: number } {
    switch (goal.type) {
        case 'essence-gather': {
            const pool = goal.scope === 'party' ? facts.partyEssence : facts.essence;
            const current = goal.essenceType !== undefined
                ? pool[goal.essenceType] ?? 0
                : resolveEssenceExchangeValue(pool);
            return { isMet: current >= goal.target, current, target: goal.target };
        }
        case 'destroy-heart':
            return { isMet: facts.isHeartShattered };
    }
}

function findProgress(progress: readonly CommissionGoalProgress[], goalId: string): CommissionGoalProgress | undefined {
    return progress.find((entry) => entry.goalId === goalId);
}

/** Whether every goal that isn't optional is complete. */
export function isCommissionComplete(
    goals: readonly CommissionGoal[],
    progress: readonly CommissionGoalProgress[],
): boolean {
    return goals
        .filter((goal) => goal.isOptional !== true)
        .every((goal) => findProgress(progress, goal.id)?.status === 'complete');
}

/**
 * Whether the heart may be damaged as far as this character's commission is
 * concerned: every destroy-heart goal has its requirements met. True when the
 * commission has no destroy-heart goal.
 */
export function isHeartGateMet(
    goals: readonly CommissionGoal[],
    progress: readonly CommissionGoalProgress[],
): boolean {
    return goals
        .filter((goal) => goal.type === 'destroy-heart')
        .every((goal) => findProgress(progress, goal.id)?.status !== 'locked');
}

/** Whether a goal is shown to the player: hidden goals appear once unlocked. */
export function isCommissionGoalVisible(goal: CommissionGoal, progress: CommissionGoalProgress | undefined): boolean {
    return goal.isHiddenWhileLocked !== true || (progress !== undefined && progress.status !== 'locked');
}

/**
 * The essence a character hands over to the issuer for completed gather goals:
 * typed goals take their type first, then untyped goals take exchange value
 * from what is left, cheapest types first. Never takes more than `essence`.
 */
export function resolveCommissionHandOver(
    goals: readonly CommissionGoal[],
    progress: readonly CommissionGoalProgress[],
    essence: Partial<EssencePool>,
): Partial<EssencePool> {
    const handedOverGoals = goals.filter((goal) => goal.type === 'essence-gather'
        && goal.isHandedOver !== false
        && findProgress(progress, goal.id)?.status === 'complete');
    return takeForGatherGoals(handedOverGoals, essence);
}

/**
 * The run essence a character's gather goals hold in reserve: what counts
 * toward each goal, up to its target, taken in the hand-over's order. Every
 * gather goal holds, whatever its status, and whether or not it hands its
 * essence over: each is judged on what is carried out at the end, so spending
 * held essence (coalescing, re-manifesting) could undo it. Party-scope goals
 * hold from each member's own essence. Never holds more than `essence`.
 */
export function resolveCommissionReservedEssence(
    goals: readonly CommissionGoal[],
    essence: Partial<EssencePool>,
): Partial<EssencePool> {
    return takeForGatherGoals(goals.filter((goal) => goal.type === 'essence-gather'), essence);
}

/**
 * Takes each gather goal's target from `essence`: typed goals take their type
 * first, then untyped goals take exchange value from what is left, cheapest
 * types first. Never takes more than `essence`.
 */
function takeForGatherGoals(goals: readonly CommissionGoal[], essence: Partial<EssencePool>): Partial<EssencePool> {
    const remaining: Partial<EssencePool> = { ...essence };
    const taken: Partial<EssencePool> = {};
    const take = (part: Partial<EssencePool>): void => {
        for (const type of getAllEssenceTypes()) {
            const amount = part[type] ?? 0;
            if (amount > 0) {
                taken[type] = (taken[type] ?? 0) + amount;
                remaining[type] = (remaining[type] ?? 0) - amount;
            }
        }
    };

    const typedFirst = [...goals].sort((a, b) => Number(isUntyped(a)) - Number(isUntyped(b)));
    for (const goal of typedFirst) {
        if (goal.type !== 'essence-gather') {
            continue;
        }
        if (goal.essenceType !== undefined) {
            take({ [goal.essenceType]: Math.min(remaining[goal.essenceType] ?? 0, goal.target) });
        } else {
            take(resolveEssenceToTake(remaining, goal.target));
        }
    }
    return taken;
}

function isUntyped(goal: CommissionGoal): boolean {
    return goal.type === 'essence-gather' && goal.essenceType === undefined;
}
